/**
 * MentionText - a note's words with the teammates it names highlighted
 * (docs/plans/crm-report-builder-search-collab-plan.md A3.4 step 1 and B3.5; WP M-6). X-3 renders it
 * in the timeline's note card in place of the plain body.
 *
 * The names come from the note's own record, metadata.mentions (`[{ user_id, name }]`, the names as
 * they were when the note was written), matched by M-2's mentionSegments with the same rule the
 * composer used at Post: what was picked is what is highlighted, while a name typed without picking,
 * an email address and a name changed after picking stay plain text. Joining the pieces gives the body
 * back exactly, and the wrapper is an inline span, so the card's whitespace-pre-wrap paragraph, its
 * clamp and its "Show more" behave as before. An empty body renders nothing.
 */
import { Fragment } from 'react';
import { mentionSegments } from './mentionLogic';
import type { NoteMentionMeta } from './types';

export interface MentionTextProps {
  /** The note's body. */
  body: string | null | undefined;
  /** The note's metadata.mentions; missing or malformed entries highlight nothing. */
  mentions?: ReadonlyArray<NoteMentionMeta> | null;
  /** Classes of the wrapping span. */
  className?: string;
}

/** The highlight of one "@Name": the brand tint, as a mention is a pointer to a teammate. */
export const MENTION_HIGHLIGHT_CLS =
  'rounded bg-primary-50 px-0.5 font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300';

export default function MentionText({ body, mentions, className }: MentionTextProps) {
  const segments = mentionSegments(body, mentions);
  if (!segments.length) return null;
  return (
    <span data-mention-text="" className={className}>
      {segments.map((segment, index) =>
        segment.kind === 'mention' ? (
          <span key={index} data-mention-user={segment.user_id} className={MENTION_HIGHLIGHT_CLS}>
            {segment.text}
          </span>
        ) : (
          <Fragment key={index}>{segment.text}</Fragment>
        ),
      )}
    </span>
  );
}
